import { useState, useEffect, useRef } from 'react'
import { X, Euro, CreditCard, Building, Gift, CheckCircle, AlertTriangle, Copy, Check, Upload, FileText, Image, ShieldAlert, Truck } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface Props {
  isOpen: boolean
  onClose: () => void
  returnData: {
    id: number
    code: string
    customer_name: string
    customer_email: string
    product_amount: number
    shipping_cost: number
    refund_amount: number
    test_result: string
    payment_method?: string | null
    refund_method?: string | null
    refund_iban?: string | null
    refund_iban_holder?: string | null
    is_prestashop_customer?: boolean
  }
  onRefunded: () => void
}

const CARD_PAYMENTS = ['redsys', 'stripe']

function formatIban(iban: string): string {
  const clean = iban.replace(/\s/g, '')
  return clean.replace(/(.{4})/g, '$1 ').trim()
}

export default function RefundReturnModal({ isOpen, onClose, returnData, onRefunded }: Props) {
  const isCardPayment = CARD_PAYMENTS.includes(returnData.payment_method || '')
  const customerPreference = returnData.refund_method

  const getDefaultMethod = () => customerPreference || (isCardPayment ? 'redsys' : 'transfer')

  const [refundMethod, setRefundMethod] = useState(getDefaultMethod())
  const [customAmount, setCustomAmount] = useState<string | null>(null)
  const [createRectificative, setCreateRectificative] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [copiedIban, setCopiedIban] = useState(false)
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  // Shipping adjustment state
  const [waiveReturnShipping, setWaiveReturnShipping] = useState(false)
  const [waiveAllShipping, setWaiveAllShipping] = useState(false)
  const [originalOrderShipping, setOriginalOrderShipping] = useState<number | null>(null)
  const [loadingShipping, setLoadingShipping] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setRefundMethod(getDefaultMethod())
      setCustomAmount(null)
      setReceiptFile(null)
      setCopiedIban(false)
      setShowConfirmation(false)
      setWaiveReturnShipping(false)
      setWaiveAllShipping(false)
      setOriginalOrderShipping(null)

      // Fetch original order shipping
      if (returnData.shipping_cost > 0) {
        setLoadingShipping(true)
        api.get(`/returns/admin/${returnData.id}/original-shipping`)
          .then(res => {
            setOriginalOrderShipping(res.data.original_order_shipping ?? 0)
          })
          .catch(() => setOriginalOrderShipping(null))
          .finally(() => setLoadingShipping(false))
      }
    }
  }, [isOpen, returnData.id])

  // Compute adjustment type
  const adjustmentType = waiveAllShipping ? 'waive_all_shipping'
    : waiveReturnShipping ? 'waive_return_shipping'
    : null

  // Compute refund amount based on adjustment
  const computedRefundAmount = (() => {
    if (adjustmentType === 'waive_all_shipping') {
      return returnData.product_amount + (originalOrderShipping || 0)
    }
    if (adjustmentType === 'waive_return_shipping') {
      return returnData.product_amount
    }
    return customAmount !== null ? (parseFloat(customAmount) || 0) : returnData.refund_amount
  })()

  const actualRefundAmount = computedRefundAmount

  const formatMoney = (amount: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount)

  const copyIban = () => {
    if (returnData.refund_iban) {
      navigator.clipboard.writeText(returnData.refund_iban.replace(/\s/g, ''))
      setCopiedIban(true)
      setTimeout(() => setCopiedIban(false), 2000)
    }
  }

  // Para transferencia: no necesita receipt en el formulario principal, se pide en confirmación
  const canSubmit = actualRefundAmount > 0 && !loading

  const handleRefund = async () => {
    setLoading(true)
    try {
      if (receiptFile && refundMethod === 'transfer') {
        const formData = new FormData()
        formData.append('file', receiptFile)
        await api.post(`/returns/admin/${returnData.id}/transfer-receipt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }

      const payload: Record<string, any> = {
        refund_method: refundMethod,
        create_rectificative: createRectificative,
        refund_iban: refundMethod === 'transfer' && returnData.refund_iban ? returnData.refund_iban : undefined
      }

      if (adjustmentType) {
        payload.refund_adjustment_type = adjustmentType
      } else if (customAmount !== null) {
        payload.refund_amount = parseFloat(customAmount) || 0
      }

      await api.post(`/returns/admin/${returnData.id}/refund`, payload)

      toast.success('Reembolso procesado', `${formatMoney(actualRefundAmount)} reembolsado para ${returnData.code}`)
      onRefunded()
      onClose()
    } catch (error: any) {
      toast.error('Error', error.message || 'Error al procesar reembolso')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const testResultWarning = returnData.test_result !== 'ok' && returnData.test_result !== 'pending'

  const ALL_METHODS: Record<string, { value: string; label: string; description: string; icon: any }> = {
    redsys: { value: 'redsys', label: 'Tarjeta de crédito (Redsys)', description: 'Devolución a la tarjeta con la que se realizó el pago', icon: CreditCard },
    transfer: { value: 'transfer', label: 'Transferencia bancaria', description: 'Transferencia a la cuenta bancaria del cliente', icon: Building },
    store_credit: { value: 'store_credit', label: 'Cupón descuento', description: 'Cupón para futuras compras en la tienda', icon: Gift }
  }

  const canUseCoupon = returnData.is_prestashop_customer === true

  const availableMethods = customerPreference && ALL_METHODS[customerPreference]
    ? [
        ALL_METHODS[customerPreference],
        ...(canUseCoupon && customerPreference !== 'store_credit' ? [ALL_METHODS.store_credit] : [])
      ]
    : isCardPayment
      ? [
          ALL_METHODS.redsys,
          ...(canUseCoupon ? [ALL_METHODS.store_credit] : [])
        ]
      : [
          ALL_METHODS.transfer,
          ...(canUseCoupon ? [ALL_METHODS.store_credit] : [])
        ]

  // ── Confirmación Redsys (tarjeta) ──
  const renderRedsysConfirmation = () => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden border border-slate-200 dark:border-slate-700">
      <div className="p-6 space-y-4">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
            <ShieldAlert className="w-7 h-7 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Confirmar reembolso</h3>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-300 text-center leading-relaxed">
          Está a punto de reembolsar{' '}
          <strong className="text-slate-900 dark:text-white">{formatMoney(actualRefundAmount)}</strong>
          {' '}en la tarjeta de crédito de{' '}
          <strong className="text-slate-900 dark:text-white">{returnData.customer_name}</strong>.
          {' '}El importe se devolverá automáticamente a través de Redsys.
        </p>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300 text-center font-medium">
            Este proceso no es reversible.
          </p>
        </div>

        <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Devolución:</span>
            <span className="font-medium text-slate-900 dark:text-white">{returnData.code}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Método:</span>
            <span className="font-medium text-slate-900 dark:text-white">Tarjeta (Redsys)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Importe:</span>
            <span className="font-bold text-red-600">{formatMoney(actualRefundAmount)}</span>
          </div>
        </div>
      </div>

      <div className="flex gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
        <button
          onClick={() => setShowConfirmation(false)}
          disabled={loading}
          className="flex-1 px-4 py-2.5 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 font-medium text-sm"
        >
          Cancelar
        </button>
        <button
          onClick={handleRefund}
          disabled={loading}
          className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium text-sm flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Procesando...
            </>
          ) : (
            'Sí, reembolsar'
          )}
        </button>
      </div>
    </div>
  )

  // ── Confirmación Transferencia bancaria ──
  const renderTransferConfirmation = () => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden border border-slate-200 dark:border-slate-700">
      <div className="p-6 space-y-4">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
            <Building className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Confirmar reembolso por transferencia</h3>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-300 text-center leading-relaxed">
          Para confirmar el reembolso de la devolución{' '}
          <strong className="text-slate-900 dark:text-white">{returnData.code}</strong>,
          {' '}realice una transferencia bancaria de{' '}
          <strong className="text-slate-900 dark:text-white">{formatMoney(actualRefundAmount)}</strong>
          {' '}a la siguiente cuenta:
        </p>

        {/* Datos bancarios del cliente */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800 space-y-3">
          {returnData.refund_iban ? (
            <>
              <div>
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">IBAN</p>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-base tracking-wider text-blue-900 dark:text-blue-100 font-semibold">
                    {formatIban(returnData.refund_iban)}
                  </span>
                  <button
                    onClick={copyIban}
                    className="p-1.5 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors shrink-0"
                    title="Copiar IBAN"
                  >
                    {copiedIban ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-blue-500" />}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">Titular</p>
                <p className="text-base font-semibold text-blue-900 dark:text-blue-100">
                  {returnData.refund_iban_holder || returnData.customer_name}
                </p>
              </div>
            </>
          ) : (
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                El cliente no proporcionó un IBAN. Solicítalo antes de procesar la transferencia.
              </p>
            </div>
          )}
        </div>

        {/* Upload comprobante */}
        <div>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-3 text-center">
            Cuando finalice la transferencia, adjunte el comprobante para confirmar el reembolso:
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
          />
          {receiptFile ? (
            <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              {receiptFile.type === 'application/pdf'
                ? <FileText className="w-5 h-5 text-green-600 shrink-0" />
                : <Image className="w-5 h-5 text-green-600 shrink-0" />
              }
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-800 dark:text-green-300 truncate">{receiptFile.name}</p>
                <p className="text-xs text-green-600">{(receiptFile.size / 1024).toFixed(0)} KB</p>
              </div>
              <button
                onClick={() => { setReceiptFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                className="p-1 rounded hover:bg-green-200 dark:hover:bg-green-800"
              >
                <X className="w-4 h-4 text-green-600" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full p-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600
                       hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all
                       flex flex-col items-center gap-2 text-slate-500"
            >
              <Upload className="w-6 h-6" />
              <span className="text-sm font-medium">Adjuntar comprobante de transferencia</span>
              <span className="text-xs">PDF, JPG o PNG</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
        <button
          onClick={() => { setShowConfirmation(false); setReceiptFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
          disabled={loading}
          className="flex-1 px-4 py-2.5 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 font-medium text-sm"
        >
          Cancelar
        </button>
        <button
          onClick={handleRefund}
          disabled={loading || !receiptFile}
          className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Procesando...
            </>
          ) : (
            'Confirmar reembolso'
          )}
        </button>
      </div>
    </div>
  )

  // ── Confirmación Cupón descuento ──
  const renderStoreCreditConfirmation = () => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden border border-slate-200 dark:border-slate-700">
      <div className="p-6 space-y-4">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
            <Gift className="w-7 h-7 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Confirmar cupón de descuento</h3>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-300 text-center leading-relaxed">
          Se creará un cupón de descuento por{' '}
          <strong className="text-slate-900 dark:text-white">{formatMoney(actualRefundAmount)}</strong>
          {' '}para <strong className="text-slate-900 dark:text-white">{returnData.customer_name}</strong>.
        </p>

        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800 space-y-2 text-sm">
          <div className="flex items-start gap-2">
            <Gift className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
            <span className="text-purple-700 dark:text-purple-300">
              El cupón será válido durante <strong>365 días</strong> y podrá usarse en una compra en jvs-informatica.com.
            </span>
          </div>
        </div>

        <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Devolución:</span>
            <span className="font-medium text-slate-900 dark:text-white">{returnData.code}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Cliente:</span>
            <span className="font-medium text-slate-900 dark:text-white">{returnData.customer_email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Importe del cupón:</span>
            <span className="font-bold text-purple-600">{formatMoney(actualRefundAmount)}</span>
          </div>
        </div>

        <p className="text-xs text-slate-500 text-center">
          Se enviará un email a {returnData.customer_email} con el código del cupón.
        </p>
      </div>

      <div className="flex gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
        <button
          onClick={() => setShowConfirmation(false)}
          disabled={loading}
          className="flex-1 px-4 py-2.5 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 font-medium text-sm"
        >
          Cancelar
        </button>
        <button
          onClick={handleRefund}
          disabled={loading}
          className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium text-sm flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              Procesando...
            </>
          ) : (
            'Crear cupón'
          )}
        </button>
      </div>
    </div>
  )

  const hasShippingCost = returnData.shipping_cost > 0

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-800 z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Euro className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Procesar Reembolso</h2>
              <p className="text-sm text-slate-500">{returnData.code} — {returnData.customer_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Aviso si testeo no fue OK */}
          {testResultWarning && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-400">Testeo con incidencia</p>
                  <p className="text-amber-700 dark:text-amber-300 mt-1">
                    El testeo del producto indicó: <strong>{returnData.test_result}</strong>. Considera ajustar el importe del reembolso.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Resumen de importes */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Importe productos:</span>
              <span className="font-medium">{formatMoney(returnData.product_amount)}</span>
            </div>
            {hasShippingCost && !adjustmentType && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Coste envío descontado:</span>
                <span className="font-medium text-red-600">-{formatMoney(returnData.shipping_cost)}</span>
              </div>
            )}
            {hasShippingCost && adjustmentType === 'waive_return_shipping' && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 line-through">Coste envío descontado:</span>
                <span className="font-medium text-slate-400 line-through">-{formatMoney(returnData.shipping_cost)}</span>
                <span className="text-xs text-green-600 font-medium ml-2">Exonerado</span>
              </div>
            )}
            {hasShippingCost && adjustmentType === 'waive_all_shipping' && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 line-through">Coste envío descontado:</span>
                  <span className="font-medium text-slate-400 line-through">-{formatMoney(returnData.shipping_cost)}</span>
                  <span className="text-xs text-green-600 font-medium ml-2">Exonerado</span>
                </div>
                {originalOrderShipping != null && originalOrderShipping > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Abono portes pedido original:</span>
                    <span className="font-medium text-green-600">+{formatMoney(originalOrderShipping)}</span>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between text-sm pt-2 border-t border-slate-200 dark:border-slate-700">
              <span className="text-slate-500">
                {adjustmentType ? 'Total reembolso:' : 'Reembolso calculado:'}
              </span>
              <span className="font-semibold text-lg">{formatMoney(actualRefundAmount)}</span>
            </div>
          </div>

          {/* Ajuste de portes — solo visible si hay shipping_cost */}
          {hasShippingCost && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-800 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-800 dark:text-indigo-300">Ajuste de portes</span>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={waiveReturnShipping}
                  onChange={(e) => {
                    setWaiveReturnShipping(e.target.checked)
                    if (!e.target.checked) setWaiveAllShipping(false)
                    if (e.target.checked) setCustomAmount(null)
                  }}
                  className="w-4 h-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="flex-1">
                  <span className="text-sm text-slate-700 dark:text-slate-300">No cobrar portes de devolución</span>
                  <span className="text-xs text-green-600 ml-2">+{formatMoney(returnData.shipping_cost)}</span>
                </div>
              </label>

              {waiveReturnShipping && originalOrderShipping != null && originalOrderShipping > 0 && (
                <label className="flex items-center gap-3 cursor-pointer ml-7">
                  <input
                    type="checkbox"
                    checked={waiveAllShipping}
                    onChange={(e) => {
                      setWaiveAllShipping(e.target.checked)
                      if (e.target.checked) setCustomAmount(null)
                    }}
                    className="w-4 h-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-slate-700 dark:text-slate-300">Abonar también portes del pedido original</span>
                    <span className="text-xs text-green-600 ml-2">+{formatMoney(originalOrderShipping)}</span>
                  </div>
                </label>
              )}

              {waiveReturnShipping && loadingShipping && (
                <p className="text-xs text-slate-500 ml-7">Consultando portes del pedido original...</p>
              )}
            </div>
          )}

          {/* Importe personalizado — oculto si hay ajuste activo */}
          {!adjustmentType && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Importe a reembolsar
              </label>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={returnData.product_amount}
                    value={customAmount !== null ? customAmount : returnData.refund_amount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                             bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </div>
                {customAmount !== null && (
                  <button onClick={() => setCustomAmount(null)} className="text-sm text-indigo-600 hover:text-indigo-800">
                    Restablecer
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Método de reembolso */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Método de reembolso
            </label>
            <div className="space-y-2">
              {availableMethods.map(method => {
                const Icon = method.icon
                const isSelected = refundMethod === method.value
                return (
                  <button
                    key={method.value}
                    onClick={() => setRefundMethod(method.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-400'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-indigo-600' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-medium ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-900 dark:text-white'}`}>
                          {method.label}
                        </p>
                        <p className="text-sm text-slate-500">{method.description}</p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* IBAN preview para transferencia (solo lectura) */}
          {refundMethod === 'transfer' && returnData.refund_iban && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 text-sm">
                <Building className="w-4 h-4 text-blue-600 shrink-0" />
                <span className="text-blue-700 dark:text-blue-300">
                  IBAN: <span className="font-mono font-medium">{formatIban(returnData.refund_iban)}</span>
                  {(returnData.refund_iban_holder || returnData.customer_name) && (
                    <> — Titular: <strong>{returnData.refund_iban_holder || returnData.customer_name}</strong></>
                  )}
                </span>
              </div>
            </div>
          )}

          {refundMethod === 'transfer' && !returnData.refund_iban && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span className="text-amber-700 dark:text-amber-300">
                  El cliente no proporcionó un IBAN. Solicítalo antes de procesar la transferencia.
                </span>
              </div>
            </div>
          )}

          {/* Info cupón descuento */}
          {refundMethod === 'store_credit' && (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-2 text-sm">
                <Gift className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                <span className="text-purple-700 dark:text-purple-300">
                  Se creará un cupón de descuento por <strong>{formatMoney(actualRefundAmount)}</strong> válido durante 365 días.
                </span>
              </div>
            </div>
          )}

          {/* Factura rectificativa */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="rectificative"
              checked={createRectificative}
              onChange={(e) => setCreateRectificative(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="rectificative" className="text-sm text-slate-700 dark:text-slate-300">
              Crear factura rectificativa en Factusol
            </label>
          </div>

          {/* Resumen final */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <span className="font-medium text-green-800 dark:text-green-400">Total a reembolsar:</span>
              <span className="text-2xl font-bold text-green-600">{formatMoney(actualRefundAmount)}</span>
            </div>
            <p className="text-sm text-green-700 dark:text-green-300 mt-2">
              Se enviará un email a {returnData.customer_email} confirmando el reembolso.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 sticky bottom-0 bg-white dark:bg-slate-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={() => setShowConfirmation(true)}
            disabled={!canSubmit}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            Procesar reembolso
          </button>
        </div>

        {/* Overlay de confirmación adaptado por método */}
        {showConfirmation && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20 rounded-2xl">
            {refundMethod === 'redsys' && renderRedsysConfirmation()}
            {refundMethod === 'transfer' && renderTransferConfirmation()}
            {refundMethod === 'store_credit' && renderStoreCreditConfirmation()}
          </div>
        )}
      </div>
    </div>
  )
}
